import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import BottomNav from '../components/BottomNav';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import { ArrowLeft, Send, Bot, User, Loader2, Droplets, Sparkles, Car, Fuel, ChevronRight } from 'lucide-react';

const AIAssistantPage = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  const [messages, setMessages] = useState([
    {
      id: 'welcome',
      role: 'bot',
      content: 'Olá! Sou o **Assistente Técnico HáLubri** da Sonangol.\n\nDescreva o seu veículo com os seguintes detalhes:\n- Marca e modelo\n- Ano de fabrico\n- Quilometragem actual\n- Tipo de combustível (gasolina/diesel)\n\nAssim, posso recomendar o **lubrificante ideal** para o seu motor!',
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestedProducts, setSuggestedProducts] = useState([]);
  const [products, setProducts] = useState([]);
  const messagesEndRef = useRef(null);

  // Fetch products for displaying recommended ones
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await api().get('/products');
        setProducts(res.data);
      } catch (err) {
        console.error('Error fetching products:', err);
      }
    };
    fetchProducts();
  }, [api]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { id: crypto.randomUUID(), role: 'user', content: userMessage, timestamp: new Date() }]);
    setLoading(true);
    setSuggestedProducts([]);

    try {
      const res = await api().post('/ai/recommend', { vehicle_description: userMessage });
      setMessages(prev => [...prev, { 
        id: crypto.randomUUID(),
        role: 'bot', 
        content: res.data.recommendation, 
        timestamp: new Date() 
      }]);
      if (res.data.suggested_products?.length > 0) {
        setSuggestedProducts(res.data.suggested_products);
      }
    } catch (err) {
      toast.error('Erro ao processar a recomendação. Tente novamente.');
      setMessages(prev => [...prev, { 
        id: crypto.randomUUID(),
        role: 'bot', 
        content: 'Desculpe, ocorreu um erro ao processar o seu pedido. Por favor, tente novamente ou descreva melhor o seu veículo.', 
        timestamp: new Date() 
      }]);
    }
    setLoading(false);
  };

  const quickPrompts = [
    { icon: Car, text: "Toyota Hilux 2018 diesel 150.000km" },
    { icon: Fuel, text: "Honda CG 150 moto 2020" },
    { icon: Car, text: "Hyundai Tucson 2016 gasolina 80.000km" },
    { icon: Fuel, text: "Camião Mercedes Actros 2019" }
  ];

  // Parse markdown-like formatting in bot messages
  const formatMessage = (text) => {
    if (!text) return '';
    
    // Split by lines and process
    const lines = text.split('\n');
    const elements = [];
    let listItems = [];
    
    lines.forEach((line, idx) => {
      // Bold text **text**
      const formattedLine = line.replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-zinc-900">$1</strong>');
      
      // Check if it's a list item
      if (line.trim().startsWith('- ') || line.trim().startsWith('• ')) {
        listItems.push(
          <li key={`li-${idx}`} className="flex items-start gap-2 text-sm text-zinc-700">
            <span className="text-sonangol-red mt-1">•</span>
            <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formattedLine.replace(/^[-•]\s*/, '')) }} />
          </li>
        );
      } else if (line.match(/^\d+\.\s/)) {
        // Numbered list
        const num = line.match(/^(\d+)\./)[1];
        listItems.push(
          <li key={`li-${idx}`} className="flex items-start gap-2 text-sm text-zinc-700">
            <span className="text-sonangol-red font-semibold min-w-[20px]">{num}.</span>
            <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formattedLine.replace(/^\d+\.\s*/, '')) }} />
          </li>
        );
      } else {
        // If we have accumulated list items, flush them
        if (listItems.length > 0) {
          elements.push(
            <ul key={`ul-${idx}`} className="space-y-1 my-2">
              {listItems}
            </ul>
          );
          listItems = [];
        }
        // Regular paragraph
        if (line.trim()) {
          elements.push(
            <p key={`p-${idx}`} className="text-sm text-zinc-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formattedLine) }} />
          );
        } else if (elements.length > 0) {
          elements.push(<div key={`br-${idx}`} className="h-2" />);
        }
      }
    });
    
    // Flush remaining list items
    if (listItems.length > 0) {
      elements.push(
        <ul key="ul-final" className="space-y-1 my-2">
          {listItems}
        </ul>
      );
    }
    
    return elements;
  };

  // Get product details by ID
  const getProductById = (productId) => {
    return products.find(p => p.id === productId);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-50 to-zinc-100 flex flex-col pb-20">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-zinc-200 shadow-sm">
        <div className="px-4 py-3">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate(-1)} 
              className="w-10 h-10 flex items-center justify-center rounded-xl bg-zinc-100 hover:bg-zinc-200 transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5 text-zinc-700" />
            </button>
            <div className="flex items-center gap-3 flex-1">
              <div className="w-11 h-11 bg-gradient-to-br from-sonangol-red to-sonangol-orange rounded-xl flex items-center justify-center shadow-lg shadow-red-200">
                <Bot className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-barlow font-bold text-lg text-zinc-900">Assistente IA</h1>
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                  <p className="text-xs text-zinc-500 font-manrope">Online • Recomendação de Lubrificantes</p>
                </div>
              </div>
            </div>
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-50">
              <Sparkles className="w-5 h-5 text-amber-500" />
            </div>
          </div>
        </div>
      </div>

      {/* Chat Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-6">
        <div className="space-y-5 max-w-2xl mx-auto">
          {messages.map((msg, idx) => (
            <div
              key={msg.id}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              data-testid={`message-${idx}`}
            >
              <div className={`flex gap-3 max-w-[88%] ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                {/* Avatar */}
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md ${
                  msg.role === 'user' 
                    ? 'bg-zinc-800' 
                    : 'bg-gradient-to-br from-sonangol-red to-sonangol-orange'
                }`}>
                  {msg.role === 'user' 
                    ? <User className="w-5 h-5 text-white" />
                    : <Bot className="w-5 h-5 text-white" />
                  }
                </div>
                
                {/* Message Bubble */}
                <div className={`px-4 py-3 shadow-sm ${
                  msg.role === 'user'
                    ? 'bg-gradient-to-br from-sonangol-red to-red-600 text-white rounded-2xl rounded-tr-md'
                    : 'bg-white border border-zinc-100 text-zinc-800 rounded-2xl rounded-tl-md'
                }`}>
                  {msg.role === 'user' ? (
                    <p className="text-sm font-manrope leading-relaxed">{msg.content}</p>
                  ) : (
                    <div className="font-manrope space-y-1">
                      {formatMessage(msg.content)}
                    </div>
                  )}
                  <p className={`text-xs mt-2 ${msg.role === 'user' ? 'text-red-200' : 'text-zinc-400'}`}>
                    {new Date(msg.timestamp).toLocaleTimeString('pt-AO', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            </div>
          ))}
          
          {/* Loading Indicator */}
          {loading && (
            <div className="flex justify-start animate-fade-in">
              <div className="flex gap-3 max-w-[90%]">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-sonangol-red to-sonangol-orange flex items-center justify-center shadow-md">
                  <Bot className="w-4 h-4 text-white" />
                </div>
                <div className="bg-white border border-zinc-100 px-5 py-4 rounded-2xl rounded-tl-md shadow-sm">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin text-sonangol-red" />
                    <span className="text-sm text-zinc-500 font-manrope">A analisar o seu veículo...</span>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Scroll anchor */}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Suggested Products */}
      {suggestedProducts.length > 0 && (
        <div className="px-4 py-3 bg-white border-t border-zinc-100 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
          <div className="flex items-center gap-2 mb-3">
            <Droplets className="w-4 h-4 text-sonangol-red" />
            <p className="text-sm font-semibold text-zinc-800 font-barlow">Produtos Recomendados</p>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
            {suggestedProducts.map((productId) => {
              const product = getProductById(productId);
              return (
                <button
                  key={productId}
                  onClick={() => navigate(`/product/${productId}`)}
                  className="flex-shrink-0 bg-gradient-to-br from-zinc-50 to-white border border-zinc-200 rounded-xl p-3 min-w-[160px] hover:border-sonangol-red hover:shadow-md transition-all group"
                  data-testid={`suggested-product-${productId}`}
                >
                  {product ? (
                    <div className="text-left">
                      <p className="text-xs text-sonangol-red font-semibold mb-1">{product.viscosity}</p>
                      <p className="text-sm font-semibold text-zinc-800 line-clamp-2">{product.name}</p>
                      <div className="flex items-center justify-between mt-2">
                        <p className="text-xs text-zinc-500">{product.volume_liters}L</p>
                        <ChevronRight className="w-4 h-4 text-zinc-400 group-hover:text-sonangol-red transition-colors" />
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Droplets className="w-4 h-4 text-sonangol-red" />
                      <span className="text-sm text-zinc-700">Ver Produto</span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Quick Prompts */}
      {messages.length <= 2 && !loading && (
        <div className="px-4 py-3 bg-gradient-to-t from-zinc-100 to-transparent">
          <p className="text-xs text-zinc-500 mb-3 font-manrope font-medium">💡 Exemplos rápidos:</p>
          <div className="grid grid-cols-2 gap-2">
            {quickPrompts.map((prompt, idx) => (
              <button
                key={prompt.text}
                onClick={() => setInput(prompt.text)}
                className="flex items-center gap-2 px-3 py-2.5 bg-white rounded-xl text-left border border-zinc-200 hover:border-sonangol-red hover:shadow-sm transition-all group"
                data-testid={`quick-prompt-${idx}`}
              >
                <prompt.icon className="w-4 h-4 text-zinc-400 group-hover:text-sonangol-red transition-colors flex-shrink-0" />
                <span className="text-xs text-zinc-700 line-clamp-1">{prompt.text}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="sticky bottom-16 bg-white border-t border-zinc-200 px-4 py-3 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
        <form onSubmit={sendMessage} className="flex gap-3">
          <div className="flex-1 relative">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Descreva o seu veículo..."
              className="h-12 pl-4 pr-4 bg-zinc-50 border-zinc-200 rounded-xl focus:ring-2 focus:ring-sonangol-red/20 focus:border-sonangol-red font-manrope"
              disabled={loading}
              data-testid="chat-input"
            />
          </div>
          <Button 
            type="submit" 
            className="h-12 w-12 bg-gradient-to-br from-sonangol-red to-red-600 hover:from-red-600 hover:to-red-700 rounded-xl shadow-lg shadow-red-200 p-0 transition-all hover:scale-105 disabled:opacity-50 disabled:hover:scale-100"
            disabled={loading || !input.trim()}
            data-testid="send-btn"
          >
            <Send className="w-5 h-5" />
          </Button>
        </form>
      </div>

      <BottomNav />
    </div>
  );
};

export default AIAssistantPage;
